import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FiUsers, FiKey, FiShield, FiLink, FiBell, FiFileText, FiHome,
  FiLayers, FiClock, FiUserPlus, FiSettings, FiCpu, FiGrid,
  FiCloud, FiCheckCircle, FiSearch, FiServer, FiChevronDown,
  FiBriefcase, FiUserCheck, FiActivity, FiAlertTriangle,
  FiHeart, FiGlobe, FiBarChart2, FiZap
} from 'react-icons/fi';
import { useAuth } from '@/contexts/AuthContext';
import Logo from '@/components/ui/Logo';
import { cn } from '@/lib/utils';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: FiHome },
  { to: '/analytics', label: 'Analytics', icon: FiGrid },
  { to: '/identities', label: 'Identities', icon: FiUsers },
  { to: '/identity-groups', label: 'Identity Groups', icon: FiLayers },
  { to: '/identity-relationships', label: 'Ownership Map', icon: FiUserCheck },
  { to: '/identity-correlation', label: 'Cross-Platform', icon: FiCloud },
  { to: '/credentials', label: 'Credentials', icon: FiKey },
  { to: '/credential-health', label: 'Credential Health', icon: FiHeart },
  { to: '/oauth-governance', label: 'OAuth Apps', icon: FiGlobe },
  { to: '/discovery', label: 'Discovery', icon: FiSearch },
  { to: '/policies', label: 'Policies', icon: FiShield },
  { to: '/jit-access', label: 'JIT Access', icon: FiClock },
  { to: '/attestation', label: 'Attestation', icon: FiCheckCircle },
  { to: '/blast-radius', label: 'Blast Radius', icon: FiAlertTriangle },
  { to: '/teams', label: 'Teams', icon: FiUserPlus },
  { to: '/webhooks', label: 'Webhooks', icon: FiLink },
  { to: '/alerts', label: 'Alerts', icon: FiBell },
  { to: '/audit-logs', label: 'Audit Logs', icon: FiFileText },
];

const agentItems = [
  { to: '/agents', label: 'AI Agents', icon: FiCpu },
  { to: '/agents/command-center', label: 'Command Center', icon: FiActivity },
  { to: '/agents/anomalies', label: 'Anomalies', icon: FiAlertTriangle },
  { to: '/agents/policies', label: 'Agent Policies', icon: FiShield },
  { to: '/agents/approvals', label: 'Approvals', icon: FiCheckCircle },
  { to: '/agents/discovery', label: 'Discovery', icon: FiSearch },
  { to: '/agents/mcp', label: 'MCP Servers', icon: FiServer },
  { to: '/agents/platforms', label: 'AI Platforms', icon: FiCloud },
];

const platformItems = [
  { to: '/behavior-comparison', label: 'Behavior Analysis', icon: FiBarChart2 },
  { to: '/compliance', label: 'Compliance', icon: FiCheckCircle },
  { to: '/integrations-status', label: 'Integrations Status', icon: FiZap },
];

const settingsItems = [
  { to: '/settings', label: 'General', icon: FiSettings },
  { to: '/settings/security', label: 'Security & MFA', icon: FiShield },
  { to: '/settings/integrations', label: 'Integrations', icon: FiLink },
  { to: '/settings/notifications', label: 'Notifications', icon: FiBell },
  { to: '/settings/ai-security', label: 'AI Security', icon: FiCpu },
  { to: '/settings/ai-frameworks', label: 'AI Frameworks', icon: FiCpu },
];

const adminItems = [
  { to: '/mgt/dashboard', label: 'Admin Dashboard', icon: FiGrid },
  { to: '/mgt/partners', label: 'Partners', icon: FiUserCheck },
  { to: '/msp/dashboard', label: 'MSP Portal', icon: FiBriefcase },
  { to: '/partner/dashboard', label: 'Partner Portal', icon: FiUsers },
];

const STORAGE_KEY = 'veraid_sidebar_sections';

interface SectionState {
  management: boolean;
  platform: boolean;
  agentic: boolean;
  settings: boolean;
  admin: boolean;
}

const defaultState: SectionState = {
  management: true,
  platform: true,
  agentic: true,
  settings: false,
  admin: true,
};

interface NavLinkItemProps {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  isActive: boolean;
  badge?: number;
}

function NavLinkItem({ to, label, icon: Icon, isActive, badge }: NavLinkItemProps) {
  return (
    <Link
      to={to}
      className={cn(
        'flex items-center gap-3 py-2.5 px-3 rounded-lg text-sm no-underline transition-all duration-150 relative',
        '[&_svg]:w-[18px] [&_svg]:h-[18px] [&_svg]:flex-shrink-0',
        isActive
          ? 'text-white bg-background-hover'
          : 'text-text-secondary hover:bg-background-hover hover:text-white'
      )}
    >
      <Icon />
      {label}
      {badge !== undefined && badge > 0 && (
        <span className="absolute top-2 left-5 bg-danger text-white text-[10px] font-semibold py-0.5 px-1.5 rounded-[10px] min-w-[18px] text-center leading-tight">
          {badge}
        </span>
      )}
    </Link>
  );
}

interface CollapsibleSectionProps {
  label: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function CollapsibleSection({ label, expanded, onToggle, children }: CollapsibleSectionProps) {
  return (
    <div className="mb-2">
      <button
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex items-center justify-between w-full py-2 px-3 bg-transparent border-none cursor-pointer rounded-md transition-colors duration-150 hover:bg-background-tertiary"
      >
        <span className="text-[11px] uppercase text-text-muted tracking-wide font-medium">
          {label}
        </span>
        <FiChevronDown
          className={cn(
            'w-3.5 h-3.5 text-text-muted transition-transform duration-200',
            expanded ? 'rotate-0' : '-rotate-90'
          )}
        />
      </button>
      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden flex flex-col gap-0.5 pt-1">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function Sidebar({ href, label, icon }: NavLinkItemProps) {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const isSuperAdmin = user?.isSuperAdmin ?? false;

  const [expanded, setExpanded] = useState<SectionState>(defaultState);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        setExpanded(JSON.parse(saved));
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  useEffect(() => {
    if (pathname.startsWith('/agents')) {
      setExpanded(prev => ({ ...prev, agentic: true }));
    } else if (pathname.startsWith('/settings')) {
      setExpanded(prev => ({ ...prev, settings: true }));
    } else if (pathname.startsWith('/behavior-comparison') || pathname.startsWith('/compliance') || pathname.startsWith('/integrations-status')) {
      setExpanded(prev => ({ ...prev, platform: true }));
    }
  }, [pathname]);

  const toggleSection = (section: keyof SectionState) => {
    setExpanded(prev => {
      const newState = { ...prev, [section]: !prev[section] };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
      } catch {
        // Ignore localStorage errors
      }
      return newState;
    });
  };

  return (
    <aside className="w-60 bg-background-primary border-r border-border flex flex-col h-screen fixed left-0 top-0 overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-track-transparent scrollbar-thumb-border-light hover:scrollbar-thumb-border-lighter">
      <div className="py-5 px-6 border-b border-border sticky top-0 bg-background-primary z-10">
        <Logo size={36} variant="dark" />
      </div>

      <nav className="flex-1 p-4 flex flex-col gap-1">
        <CollapsibleSection
          label="Management"
          expanded={expanded.management}
          onToggle={() => toggleSection('management')}
        >
          {navItems.map(item => {
            const isActive = item.to === '/dashboard'
              ? pathname === '/dashboard'
              : pathname.startsWith(item.to);
            return (
              <NavLinkItem
                key={item.to}
                to={item.to}
                label={item.label}
                icon={item.icon}
                isActive={isActive}
              />
            );
          })}
        </CollapsibleSection>

        <CollapsibleSection
          label="Platform Intelligence"
          expanded={expanded.platform}
          onToggle={() => toggleSection('platform')}
        >
          {platformItems.map(item => {
            const isActive = pathname.startsWith(item.to);
            return (
              <NavLinkItem
                key={item.to}
                to={item.to}
                label={item.label}
                icon={item.icon}
                isActive={isActive}
              />
            );
          })}
        </CollapsibleSection>

        <CollapsibleSection
          label="Agentic Identity"
          expanded={expanded.agentic}
          onToggle={() => toggleSection('agentic')}
        >
          {agentItems.map(item => {
            const isActive = item.to === '/agents'
              ? pathname === '/agents'
              : pathname.startsWith(item.to);
            return (
              <NavLinkItem
                key={item.to}
                to={item.to}
                label={item.label}
                icon={item.icon}
                isActive={isActive}
              />
            );
          })}
        </CollapsibleSection>

        <CollapsibleSection
          label="Settings"
          expanded={expanded.settings}
          onToggle={() => toggleSection('settings')}
        >
          {settingsItems.map(item => {
            const isActive = item.to === '/settings'
              ? pathname === '/settings'
              : pathname.startsWith(item.to);
            return (
              <NavLinkItem
                key={item.to}
                to={item.to}
                label={item.label}
                icon={item.icon}
                isActive={isActive}
              />
            );
          })}
        </CollapsibleSection>

        {isSuperAdmin && (
          <CollapsibleSection
            label="Super Admin"
            expanded={expanded.admin}
            onToggle={() => toggleSection('admin')}
          >
            {adminItems.map(item => {
              const isActive = pathname.startsWith(item.to);
              return (
                <NavLinkItem
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  icon={item.icon}
                  isActive={isActive}
                />
              );
            })}
          </CollapsibleSection>
        )}
      </nav>
    </aside>
  );
}
